import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { RiLock2Line, RiBuilding2Line } from '@remixicon/react';
import { useAuthStore } from '@/stores/authStore';
import { useIsPortfolioMode } from '@/stores/mallScopeStore';
import { PageSkeleton } from '@/components/feedback/PageSkeleton';
import { EmptyState } from '@/components/feedback/EmptyState';
import { PageHeader } from '@/components/layout/PageHeader';
import { SectionCard } from '@/components/layout/SectionCard';
import { homePathFor } from './homePath';

/**
 * OTURUM KORUMASI.
 *
 * Açılışta sessiz yenileme sürerken İSKELET gösterilir, giriş ekranı
 * DEĞİL. Aksi hâlde sayfayı yenileyen oturumu açık bir kullanıcı bir an
 * giriş ekranını görür ve "atıldım mı?" diye tereddüt eder.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const isBootstrapping = useAuthStore((s) => s.isBootstrapping);
  const location = useLocation();

  if (isBootstrapping) return <PageSkeleton />;

  if (!user) {
    // Kullanıcının gitmek istediği yol saklanır; girişten sonra oraya döner.
    return <Navigate to="/giris" replace state={{ from: location.pathname + location.search }} />;
  }

  return <>{children}</>;
}

/**
 * YETKİ KORUMASI.
 *
 * NEDEN GÜVENLİK DEĞİL, KULLANILABİLİRLİK: Gerçek koruma sunucudadır ve
 * bu bileşen kaldırılsa bile API 403 döner. Buradaki amaç, kullanıcıya
 * açamayacağı bir ekranı boş tablo ve hata yığınıyla göstermek yerine
 * neden erişemediğini açıkça söylemektir.
 */
export function RequirePermission({
  permission,
  children,
}: {
  permission: string | string[];
  children: ReactNode;
}) {
  const canAny = useAuthStore((s) => s.canAny);
  const needed = Array.isArray(permission) ? permission : [permission];

  if (canAny(needed)) return <>{children}</>;

  return (
    <div className="app-page">
      <PageHeader
        title="Erişim yetkiniz yok"
        breadcrumbs={[{ label: 'Ana Sayfa', to: '/dashboard' }]}
      />
      <SectionCard>
        <EmptyState
          icon={RiLock2Line}
          title="Bu ekranı görüntüleme yetkiniz bulunmuyor"
          description={
            `Gerekli yetki: ${needed.join(' veya ')}. ` +
            'Erişim gerekiyorsa sistem yöneticinizden rolünüzün güncellenmesini isteyin.'
          }
        />
      </SectionCard>
    </div>
  );
}


/**
 * KULLANICI TÜRÜ KORUMASI.
 *
 * Personel token'ı portala, kiracı token'ı yönetim arayüzüne giremez.
 *
 * ASIL KORUMA SUNUCUDADIR: `@StaffOnly` / `@TenantOnly` dinleyici (audience)
 * guard'ı, yanlış türdeki token'ı tek satır okumadan reddeder. Buradaki
 * amaç güvenlik eklemek değil, yanlış yere düşen kullanıcıyı 403 yığınıyla
 * karşılaştırmak yerine kendi ana sayfasına götürmektir.
 */
export function RequireUserType({
  type,
  children,
}: {
  type: 'STAFF' | 'TENANT';
  children: ReactNode;
}) {
  const user = useAuthStore((s) => s.user);

  // RequireAuth zaten üstte; burada kullanıcı yoksa render edilecek bir şey yok.
  if (!user) return null;

  if (user.userType !== type) return <Navigate to={homePathFor(user.userType)} replace />;

  return <>{children}</>;
}


/**
 * AVM KAPSAMI KORUMASI.
 *
 * BAZI EKRANLAR PORTFÖY MODUNDA ANLAMSIZDIR — VE BUNU BAŞTA SÖYLEMELİDİR.
 *
 * "Tüm AVM'ler" seçiliyken bir beyan girmek, bir ünite eklemek ya da bir
 * gider kaydetmek mümkün değildir: kaydın hangi AVM'ye yazılacağı belirsiz
 * olduğu için sunucu (ScopedPrisma) reddeder. Reddetme doğrudur ama
 * ZAMANLAMASI yanlıştı: kullanıcı formu baştan sona dolduruyor, kaydete
 * basıyor ve ancak o zaman "önce bir AVM seçin" diyen bir hata görüyordu.
 * Yapılan iş boşa gidiyordu.
 *
 * Menü yapılandırmasındaki `scope: 'mall'` işareti tam olarak bunun için
 * vardı ama hiçbir yerde uygulanmıyordu. Bu bileşen onu uygular: ekran
 * açılmadan önce durumu söyler ve kullanıcıyı AVM seçiciye yönlendirir.
 *
 * PORTFÖY KAPSAMLI ekranlar (konsolide raporlar, pano) etkilenmez.
 */
export function RequireMallScope({ children }: { children: ReactNode }) {
  const isPortfolio = useIsPortfolioMode();

  if (!isPortfolio) return <>{children}</>;

  return (
    <div className="app-page">
      <PageHeader
        title="Bu ekran için bir AVM seçin"
        breadcrumbs={[{ label: 'Ana Sayfa', to: '/dashboard' }]}
      />
      <SectionCard>
        <EmptyState
          icon={RiBuilding2Line}
          title="Şu anda tüm AVM'ler görüntüleniyor"
          description={
            'Bu ekranda kayıt oluşturulup güncellenebildiği için tek bir AVM seçili olmalıdır: ' +
            'kaydın hangi AVM’ye yazılacağı portföy modunda belirsizdir. ' +
            'Üst çubuktaki AVM seçicisinden bir AVM seçtiğinizde ekran açılır.'
          }
        />
      </SectionCard>
    </div>
  );
}

/**
 * Zaten giriş yapmış kullanıcıyı giriş ekranından uzak tutar.
 *
 * Aksi hâlde kullanıcı geri tuşuyla giriş ekranına düşer ve oturumu açık
 * olmasına rağmen yeniden giriş yapması gerektiğini sanır.
 */
export function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const isBootstrapping = useAuthStore((s) => s.isBootstrapping);
  const location = useLocation();

  if (isBootstrapping) return <PageSkeleton />;

  if (user) {
    /*
      KULLANICI GİTMEK İSTEDİĞİ YERE DÖNER, PANOYA DEĞİL.

      `RequireAuth` yönlendirirken hedefi `state.from` içinde saklıyordu
      ama burada yok sayılıyordu. Sonuç: /kiracilar adresi yenilendiğinde
      ya da yer imi ile açıldığında kullanıcı panoya düşüyordu. Derin
      bağlantı paylaşmak da bu yüzden çalışmıyordu — bildirim ve arama
      sonuçlarının tamamı derin bağlantıdır.

      Yalnızca uygulama içi göreli yollar kabul edilir: `state` kullanıcı
      tarafından üretilebilir bir değerdir ve mutlak bir adres kabul etmek
      açık yönlendirme (open redirect) zafiyeti olurdu.
    */
    const requested = (location.state as { from?: unknown } | null)?.from;
    const home = homePathFor(user.userType);

    const isSafe =
      typeof requested === 'string' && requested.startsWith('/') && !requested.startsWith('//');

    /*
      HEDEF, KULLANICININ TÜRÜNE DE UYMALIDIR.

      Kiracı kullanıcısının elindeki bağlantı /kiracilar olabilir (paylaşılan
      bir link, eski bir yer imi). Oraya götürmek `RequireUserType`
      tarafından hemen geri çevrilmek demektir — kullanıcı iki yönlendirme
      görür ve nereye gittiğini anlamaz. Türüne uymayan hedef sessizce
      kendi ana sayfasına düşer.
    */
    const matchesAudience =
      user.userType === 'TENANT' ? isSafe && requested.startsWith('/portal') : isSafe;

    return <Navigate to={matchesAudience ? (requested as string) : home} replace />;
  }

  return <>{children}</>;
}
